import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';
import {
  ProductDetailsInputValidationSchema,
  SinglePriceInputObjectSchema,
  VariantOptionValidationRequestSchema,
  VariantRequestValidationSchema,
} from './create';
import { orderBumpCardInputFields } from './orderBumpCard';

const createShape = ProductDetailsInputValidationSchema.shape;

const UpdateProductPriceValidationSchema = SinglePriceInputObjectSchema.partial()
  .refine(data => !(data.recurring && data.paymentPlan), {
    message:
      "Cannot specify both 'recurring' and 'paymentPlan'. Use 'recurring' for subscriptions or 'paymentPlan' for payment plans.",
    path: ['paymentPlan'],
  })
  .refine(data => !(data.payWhatYouWant === true && data.paymentPlan), {
    message: 'Pay What You Want does not apply to payment plans.',
    path: ['payWhatYouWant'],
  });

const UpdateVariantValidationSchema = VariantRequestValidationSchema.extend({
  id: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Existing variant id. Include it to keep (and edit) an existing variant; omit it to create a new one.',
  }),
  options: z
    .array(
      VariantOptionValidationRequestSchema.extend({
        id: ObjectIdStringValidationSchema.optional().meta({
          description:
            'Existing option id. Include it to keep (and edit) an existing option; omit it to create a new one.',
        }),
      }),
    )
    .min(1)
    .meta({ description: 'Options for this variant. At least one is required.' }),
});

const productUpdateCoreShape = {
  title: z.string().max(200).nullish().meta({
    description: 'Product title.',
    example: 'Premium Course',
  }),
  description: createShape.description,
  price: UpdateProductPriceValidationSchema.optional().meta({
    description:
      "Patch the product's single price - top-level fields you omit keep their value, but `recurring` and `paymentPlan` are replaced whole: " +
      'omitted sub-fields are not kept (interval defaults to month, intervalCount to 1, and trialPeriodDays, billingCycleAnchorConfig and startDate are dropped), ' +
      'so read the current config with `get_product` and re-send it in full when touching either. ' +
      'Valid only while the product has at most one price; products with multiple prices must be updated through `prices[]`. Ignored when `prices` is also sent. ' +
      '`billingType` and `payWhatYouWant` are fixed once a price exists. Cannot be null.',
  }),
  sku: createShape.sku,
  hasUnlimitedStock: createShape.hasUnlimitedStock,
  stock: createShape.stock,
  taxBehavior: createShape.taxBehavior,
  taxCode: createShape.taxCode,
  fixedTaxRateIds: z.array(ObjectIdStringValidationSchema).max(1).optional().meta({
    description:
      'Tax Rate IDs applied to this product (at most one). Send `[]` to clear; null is not accepted. Call `list_tax_rates` to find an existing rate.',
  }),
  imageIds: z.array(ObjectIdStringValidationSchema).optional().meta({
    description:
      'Replaces the product images with these image file IDs (upload with `upload_file`). Send `[]` to remove all images; null is not accepted.',
  }),
  fileIds: createShape.fileIds.nullish(),
  variantsRequired: createShape.variantsRequired,
  variants: z
    .array(UpdateVariantValidationSchema)
    .nullish()
    .meta({
      description:
        'Replaces the full variant list; nothing is merged from the stored variants. Read the current variants with `get_product` first and include `id` on every variant and option you want to keep; ' +
        'entries without an id are created and existing ones missing from the list are removed. Send `[]` to remove all variants. ' +
        'Three fields do not round-trip from `get_product`: re-send each option image as `imageId: option.image.fileId` or it is dropped; option `discountedFromPrice` is not returned at all, so re-supply it or it is lost; and an option returned with `type: "pay_what_you_want"` must be sent back as `type: "one_time"` with `payWhatYouWant: true`. ' +
        'To link a discount to specific options, give those options a `key` and reference it in `discounts[].variantOptionIds[].key` in the same request.',
    }),
  discounts: createShape.discounts,
  limitSubscriptions: createShape.limitSubscriptions,
  limitPayments: createShape.limitPayments,
  enableFileAccessForInactiveSubscriptions: createShape.enableFileAccessForInactiveSubscriptions,
  generateLicenseKeys: createShape.generateLicenseKeys,
  prices: createShape.prices.meta({
    description:
      'Replaces the full price list. Read the current prices with `get_product` first and include `id` on every price you want to keep; ' +
      'entries without an id are created and existing prices missing from the list are removed. At least one entry must be enabled. ' +
      '`billingType` and `payWhatYouWant` cannot change on an existing price. Takes precedence over `price` when both are sent.',
  }),
  defaultPriceId: z.string().optional().meta({
    description:
      'ID of the price pre-selected at checkout. Must reference an existing price `id` (after this update is applied). Cannot be cleared; set a different price instead.',
  }),
  pricePicker: createShape.pricePicker,
};

const productUpdateBodyShape = {
  ...productUpdateCoreShape,
  ...orderBumpCardInputFields,
};

// Iterated by the MCP tool; kept in sync with the API's update schema by
// apps/api/src/products/schema/productUpdateFields.driftGuard.test.ts.
export const PRODUCT_UPDATE_FIELDS = Object.keys(productUpdateBodyShape) as ReadonlyArray<
  keyof typeof productUpdateBodyShape
>;

const hasAtLeastOnePrice = (data: { prices?: unknown[] | null }) =>
  !data.prices || data.prices.length >= 1;
const hasAnEnabledPrice = (data: { prices?: Array<{ enabled?: boolean | null }> | null }) =>
  !data.prices || data.prices.some(p => p.enabled !== false);

const AT_LEAST_ONE_PRICE_REFINE_OPTIONS = {
  message: 'prices must contain at least one entry.',
  path: ['prices'],
};
const ENABLED_PRICE_REFINE_OPTIONS = {
  message: 'At least one price must be enabled.',
  path: ['prices'],
};

export const ProductUpdateBodyValidationSchema = z
  .object(productUpdateBodyShape)
  .strict()
  .refine(hasAtLeastOneField, {
    message: 'At least one field must be provided.',
  })
  .refine(hasAtLeastOnePrice, AT_LEAST_ONE_PRICE_REFINE_OPTIONS)
  .refine(hasAnEnabledPrice, ENABLED_PRICE_REFINE_OPTIONS);

export const UpdateProductInputValidationSchema = z
  .object({
    productId: ObjectIdStringValidationSchema.meta({
      description:
        'The ID of the product to update. Find it on a page response as `data.product.id` (see `get_checkout_page`).',
    }),
    // Order bump card copy stays out of the MCP tool until order bumps launch there.
    ...productUpdateCoreShape,
  })
  .refine(
    value => {
      const { productId: _productId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one update field must be provided.' },
  )
  .refine(hasAtLeastOnePrice, AT_LEAST_ONE_PRICE_REFINE_OPTIONS)
  .refine(hasAnEnabledPrice, ENABLED_PRICE_REFINE_OPTIONS);

export type UpdateProductInput = z.infer<typeof UpdateProductInputValidationSchema>;
